import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { close, pencilSharp } from 'ionicons/icons';
import React from 'react';

import { ModalService } from '@biteinc/core-react';
import { OrderChannel, OrderChannelHelper } from '@biteinc/enums';
import { Strings } from '@biteinc/localization';

import { localizeStr, useLocalize } from '~/app/js/localization/localization';
import type { EnhancedLocation } from '~/helpers';
import { useLocation, useStore } from '~/stores';

import GcnOpeningSequenceManager from '../app/js/gcn_opening_sequence_manager';
import { GcnFulfillmentHelper } from '../helpers';
import { FulfillmentMethodInfo } from './fulfillment-method-info';
import { StoreInfo } from './store-info';

function showStoreInfoModal(location: EnhancedLocation): void {
  const hide = ModalService.showCustomModal({
    cssClass: 'store-info-modal',
    children: (
      <>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton
                aria-label={localizeStr(Strings.CLOSE)}
                color="light"
                onClick={() => {
                  hide();
                }}
              >
                <IonIcon
                  aria-hidden="true"
                  slot="icon-only"
                  icon={close}
                />
              </IonButton>
            </IonButtons>
            <IonTitle className="store-info-modal-title">
              {localizeStr(Strings.STORE_INFO)}
            </IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="store-info-content">
          <StoreInfo location={location} />
        </IonContent>
      </>
    ),
  });
}

function FulfillmentHeader(): JSX.Element | null {
  const str = useLocalize();
  const location = useLocation();
  const cart = useStore((store) => store.cart);

  function getOtherOrderAheadChannelLink(): { url: string; text: JSX.Element | string } | null {
    const otherOrderAheadChannelUrl = location.orderAheadChannelUrls?.find(({ orderChannel }) => {
      return orderChannel !== location.orderChannel;
    });
    if (!otherOrderAheadChannelUrl) {
      return null;
    }
    switch (otherOrderAheadChannelUrl.orderChannel) {
      case OrderChannel.Catering:
        return {
          url: otherOrderAheadChannelUrl.url,
          text: str(Strings.SWITCH_TO_CATERING),
        };
      case OrderChannel.Web:
        return {
          url: otherOrderAheadChannelUrl.url,
          text: str(Strings.SWITCH_TO_WEB),
        };
    }
  }

  function LocationNameLabel(): JSX.Element {
    return (
      // Use a disabled button to keep the CSS consistent
      <IonButton
        className="location-name-label"
        fill="clear"
        size="small"
        disabled
      >
        <h3
          className="location-name"
          aria-level={1}
        >
          {location.name}
        </h3>
        <IonIcon
          icon={pencilSharp}
          color="medium"
          slot="end"
        />
      </IonButton>
    );
  }

  const { fulfillmentMethod, deliveryAddress } = cart;
  const isOrderAhead = OrderChannelHelper.isOrderAhead(location.orderChannel);
  const fullAddress = location.fullAddress;
  const addressStr = GcnFulfillmentHelper.addressToString(fullAddress);
  const otherOrderAheadChannelLink = getOtherOrderAheadChannelLink();

  const fulfillmentMethodInfo =
    fulfillmentMethod && location.canChangeDiningOptions() ? (
      // Fulfillment method and time
      <FulfillmentMethodInfo
        fulfillmentMethod={fulfillmentMethod}
        pickupAtIso={cart.pickupAtIso}
        enableItemClick={true}
        {...(deliveryAddress && { deliveryAddress })}
        onEdit={() => {
          const openingSequenceManager = new GcnOpeningSequenceManager();
          openingSequenceManager.start();
        }}
      />
    ) : null;

  const otherOrderAheadChannelLinkButton = otherOrderAheadChannelLink ? (
    <IonButton
      className="fulfillment-secondary-link"
      color="medium"
      fill="clear"
      onClick={() => {
        window.location.href = otherOrderAheadChannelLink.url;
      }}
    >
      {otherOrderAheadChannelLink.text}
    </IonButton>
  ) : null;

  if (gcn.org.orgCode) {
    // The location name moved to the top bar. What remains reads as two label/action lines:
    // method and time above, address and store information below.
    return (
      <div className="fulfillment-info-container">
        {fulfillmentMethodInfo}
        {fullAddress ? (
          <div className="location-meta-row">
            <span className="location-meta-address">{addressStr}</span>
            <button
              type="button"
              className="location-meta-store-info"
              onClick={() => {
                showStoreInfoModal(location);
              }}
            >
              {str(Strings.STORE_INFO)}
            </button>
          </div>
        ) : null}
        {otherOrderAheadChannelLinkButton}
      </div>
    );
  }

  return (
    <div className="fulfillment-info-container">
      {isOrderAhead ? LocationNameLabel() : null}
      {fulfillmentMethodInfo}
      {isOrderAhead && fullAddress ? (
        <IonItem
          lines="none"
          className="location-address-container"
        >
          <IonLabel className="location-address">{addressStr}</IonLabel>
        </IonItem>
      ) : null}
      {fullAddress &&
      location.openingHoursByFulfillmentMethod?.length &&
      // show store info only on web or catering
      [OrderChannel.Web, OrderChannel.Catering].includes(location.orderChannel) ? (
        <IonButton
          className="fulfillment-secondary-link"
          color="medium"
          fill="clear"
          onClick={() => {
            showStoreInfoModal(location);
          }}
        >
          {str(Strings.STORE_INFORMATION)}
        </IonButton>
      ) : null}
      {otherOrderAheadChannelLinkButton}
    </div>
  );
}

export { FulfillmentHeader };
